<!--
  Why something couldn't load, in plain words: the words of a failed
  Remote (runtime/remote.ts `statusOf`), Try again when `onretry` is
  given, and the request behind a collapsed Details disclosure. A rate
  limit says which limit it is and counts down to when it lifts.

  It says the load is tried again by itself only for `retry`, the
  `Scheduled` retry a runtime store armed, while it is pending.
-->
<script lang="ts">
  import type { Failure } from '../api/failure';
  import { failed, manual, statusOf, type Retry } from '../runtime/remote';
  import FailureDetails from './FailureDetails.svelte';

  interface Props {
    failure: Failure;
    /** What was loading, as a noun phrase: "this section's search". */
    what: string;
    onretry?: () => void;
    /** The retry the store armed (runtime/effects.ts); manual when not given. */
    retry?: Retry;
  }
  let { failure, what, onretry, retry }: Props = $props();

  const limit = $derived(failure.cause === 'rate_limited' ? failure.rateLimit : null);
  let now = $state(Date.now());
  $effect(() => {
    if (!limit && retry?.kind !== 'scheduled') return;
    now = Date.now();
    const tick = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(tick);
  });
  const words = $derived(statusOf(failed(failure, retry ?? manual), what, now));
</script>

<div class="load-error" role="status">
  <p>
    <strong>{words.summary}</strong>
    {#if words.next}<span aria-live="off">{words.next}</span>{/if}
    {#if words.advice}<span class="muted">{words.advice}</span>{/if}
    {#if onretry}<button class="btn small" type="button" onclick={onretry}>Try again</button>{/if}
  </p>
  <FailureDetails {failure} {what} />
</div>

<style>
  .load-error {
    display: grid;
    gap: 6px;
    padding: 14px 16px;
    background: var(--surface);
    border: 1px dashed var(--line);
    border-radius: 10px;
    font-size: 13px;
    min-width: 0;
  }
  .load-error p {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    align-items: baseline;
  }
  strong {
    font-weight: 600;
  }
</style>
